/**
 * Compact, reusable pagination bar.
 * Layout: [Showing X–Y] [Rows dropdown] | [Prev] Page N of M [Next]
 * Use in tables, list views, etc. Theme via `variant` or MUI theme.
 */
import React from 'react';
import { Box, Typography, Select, MenuItem } from '@mui/material';
import { ChevronLeft as ChevronLeftIcon, ChevronRight as ChevronRightIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';

const PAGINATION_VARIANTS = {
  default: {
    pillBg: '#f1f5f9',
    pillColor: '#64748b',
    border: '#e2e8f0',
    active: '#2563eb',
    activeBg: 'rgba(37,99,235,0.08)',
    hoverBg: 'rgba(37,99,235,0.12)',
    disabled: '#94a3b8',
    text: '#334155',
  },
  green: {
    pillBg: '#f5f8f6',
    pillColor: '#526b5c',
    border: '#dce8e1',
    active: '#0a6b3e',
    activeBg: 'rgba(10,107,62,0.08)',
    hoverBg: 'rgba(10,107,62,0.12)',
    disabled: '#8eaa98',
    text: '#253b2c',
  },
  primary: {
    pillBg: '#f1f5f9',
    pillColor: '#64748b',
    border: '#e2e8f0',
    active: '#1976d2',
    activeBg: 'rgba(25,118,210,0.08)',
    hoverBg: 'rgba(25,118,210,0.12)',
    disabled: '#94a3b8',
    text: '#334155',
  },
};

const Pagination = ({
  totalCount = 0,
  currentPage = 1,
  pageSize = 50,
  totalPages: totalPagesProp,
  hasNext,
  hasPrevious,
  onPageChange,
  onPageSizeChange,
  showPageSizeSelector = true,
  showRangeInfo = true,
  showPageInfo = true,
  rowsPerPageOptions = [25, 50, 100, 200],
  variant = 'default',
  formatResultsCount = (n) => (n != null ? Number(n).toLocaleString() : '0'),
  slotStart,
  sx = {},
}) => {
  const totalPages = totalPagesProp ?? Math.max(1, Math.ceil(totalCount / pageSize));
  const startItem = totalCount > 0 ? (currentPage - 1) * pageSize + 1 : 0;
  const endItem = Math.min(currentPage * pageSize, totalCount);

  const isPrevDisabled =
    totalCount === 0 ||
    (typeof hasPrevious === 'boolean' ? !hasPrevious : currentPage <= 1);
  const isNextDisabled =
    totalCount === 0 ||
    (typeof hasNext === 'boolean' ? !hasNext : currentPage >= totalPages);

  const colors = typeof variant === 'string' ? PAGINATION_VARIANTS[variant] || PAGINATION_VARIANTS.default : { ...PAGINATION_VARIANTS.default, ...variant };

  const handlePageChange = (direction) => {
    if (!onPageChange) return;
    const next = currentPage + direction;
    if (next >= 1 && next <= totalPages) onPageChange(next);
  };

  const handlePageSizeChange = (e) => {
    const val = e?.target?.value != null ? parseInt(e.target.value, 10) : pageSize;
    if (!isNaN(val) && onPageSizeChange) onPageSizeChange(val);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        flexWrap: 'wrap',
        minHeight: 32,
        py: 0.5,
        ...sx,
      }}
    >
      {slotStart}

      {showRangeInfo && (
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            px: 1.25,
            py: 0.5,
            borderRadius: '8px',
            bgcolor: colors.pillBg,
          }}
        >
          <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 500, color: colors.pillColor }}>
            {totalCount > 0 ? `Showing ${formatResultsCount(startItem)}–${formatResultsCount(endItem)}` : 'No results'}
          </Typography>
        </Box>
      )}

      {showPageSizeSelector && (
        <Select
          size="small"
          value={pageSize}
          onChange={handlePageSizeChange}
          disabled={totalCount === 0}
          IconComponent={ExpandMoreIcon}
          sx={{
            minWidth: 56,
            height: 28,
            fontSize: '0.75rem',
            fontWeight: 600,
            color: colors.text,
            '& .MuiSelect-select': { py: 0.5, px: 1 },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.border },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.active },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: colors.active, borderWidth: 1 },
          }}
          variant="outlined"
        >
          {rowsPerPageOptions.map((n) => (
            <MenuItem key={n} value={n} sx={{ fontSize: '0.75rem' }}>
              {n}
            </MenuItem>
          ))}
        </Select>
      )}

      <Box
        sx={{
          width: '1px',
          height: 20,
          bgcolor: colors.border,
          flexShrink: 0,
        }}
      />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Box
          component="button"
          type="button"
          onClick={() => handlePageChange(-1)}
          disabled={isPrevDisabled}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 28,
            height: 28,
            border: `1px solid ${colors.border}`,
            borderRadius: '6px',
            bgcolor: 'transparent',
            color: isPrevDisabled ? colors.disabled : colors.text,
            cursor: isPrevDisabled ? 'not-allowed' : 'pointer',
            '&:hover': !isPrevDisabled && { borderColor: colors.active, bgcolor: colors.hoverBg, color: colors.active },
          }}
        >
          <ChevronLeftIcon sx={{ fontSize: 18 }} />
        </Box>

        {showPageInfo && (
          <Typography variant="body2" sx={{ minWidth: 72, textAlign: 'center', fontSize: '0.75rem', fontWeight: 600, color: colors.text, px: 0.5 }}>
            Page {totalCount > 0 ? currentPage : 0} of {totalCount > 0 ? totalPages : 0}
          </Typography>
        )}

        <Box
          component="button"
          type="button"
          onClick={() => handlePageChange(1)}
          disabled={isNextDisabled}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 28,
            height: 28,
            border: `1px solid ${colors.border}`,
            borderRadius: '6px',
            bgcolor: 'transparent',
            color: isNextDisabled ? colors.disabled : colors.active,
            cursor: isNextDisabled ? 'not-allowed' : 'pointer',
            '&:hover': !isNextDisabled && { borderColor: colors.active, bgcolor: colors.hoverBg },
          }}
        >
          <ChevronRightIcon sx={{ fontSize: 18 }} />
        </Box>
      </Box>
    </Box>
  );
};

export default Pagination;
export { PAGINATION_VARIANTS };
